import { useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { Screen } from '../components/Screen'
import { EmptyState, ErrorState, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { athleteMeta, isPaused, matchesQuery, pausedLast } from '../data/athletes'
import { useAthletes, useGroups } from '../data/hooks'
import { liveGroupId } from '../../utils/liveGroup'
import type { Athlete } from '../data/types'
import { BulkSheet, type BulkKind } from './AthletesBulk'

/** Долгое нажатие на строку - режим выбора (S07). */
const LONG_PRESS_MS = 500

type Filter = 'all' | 'group' | 'none'

/** Отбор Штаба (действие navigate_athletes): кого показать и как это назвал Штаб. */
export interface HubFilter {
  ids: string[]
  label: string
}

/**
 * S07 «Спортсмены»: найти человека и отличить однофамильцев по группе и
 * возрасту. Поиск и фильтр лежат в адресе: возврат из карточки их сохраняет.
 * Отбор Штаба приходит в состоянии перехода (не в адресе: список ID туда не
 * кладём) и живёт в истории, поэтому тоже переживает возврат из карточки.
 */
export function AthletesScreen() {
  const { now } = useEnv()
  const navigate = useNavigate()
  const location = useLocation()
  const hubFilter = (location.state as { hubFilter?: HubFilter } | null)?.hubFilter
  const hubIds = hubFilter ? new Set(hubFilter.ids) : null
  const [params, setParams] = useSearchParams()
  const athletes = useAthletes()
  const groups = useGroups()
  // Групповой выбор (S07, этап 5 шаг 4): явный режим, выбор не в адресе (там
  // не место списку id), переживает смену фильтра.
  const [selecting, setSelecting] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  // Снимок выбранных на момент открытия шторки: список в ней не меняется,
  // пока идёт действие (удалённые пропали бы из списка посреди итога).
  const [bulk, setBulk] = useState<{ kind: BulkKind; athletes: Athlete[] } | null>(null)
  const press = useRef<{ timer?: ReturnType<typeof setTimeout>; fired: boolean }>({ fired: false })

  const query = params.get('q') ?? ''
  const filter: Filter = (['all', 'group', 'none'] as const).find((f) => f === params.get('f')) ?? 'all'
  const groupId = params.get('g') ?? ''

  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params)
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v)
      else p.delete(k)
    }
    setParams(p, { replace: true, state: location.state })
  }

  const groupName = useMemo(() => new Map((groups.data ?? []).map((g) => [g.id, g.name])), [groups.data])
  const all = athletes.data ?? []
  // Группа удалена (мягко) - спортсмен без группы, хотя ссылка в данных осталась.
  const groupOf = (a: { group_id?: string }) => liveGroupId(a.group_id, groups.data)
  const withoutGroup = all.filter((a) => !groupOf(a)).length
  const today = now()

  const visible = pausedLast(all).filter((a) => {
    if (hubIds && !hubIds.has(a.id)) return false
    if (!matchesQuery(a, query)) return false
    if (filter === 'none') return !groupOf(a)
    if (filter === 'group' && groupId) return groupOf(a) === groupId
    return true
  })

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const startSelecting = (id?: string) => {
    setSelecting(true)
    setSelected(id ? new Set([id]) : new Set())
  }
  const stopSelecting = () => {
    setSelecting(false)
    setSelected(new Set())
    setBulk(null)
  }
  const pressStart = (id: string) => {
    press.current.fired = false
    clearTimeout(press.current.timer)
    press.current.timer = setTimeout(() => {
      press.current.fired = true
      startSelecting(id)
    }, LONG_PRESS_MS)
  }
  const pressEnd = () => clearTimeout(press.current.timer)
  const chosen = all.filter((a) => selected.has(a.id))
  const hiddenChosen = chosen.filter((a) => !visible.some((v) => v.id === a.id)).length
  const allVisibleChosen = visible.length > 0 && visible.every((a) => selected.has(a.id))

  let body
  if (athletes.isPending) {
    body = <Skeleton rows={4} label="Загружаю спортсменов" />
  } else if (athletes.isError) {
    body = <ErrorState title="Не удалось загрузить спортсменов" onRetry={() => void athletes.refetch()} />
  } else if (all.length === 0) {
    body = (
      <EmptyState
        title="Спортсменов пока нет"
        text="Добавьте первого спортсмена: имя и группа, остальное можно заполнить позже."
      />
    )
  } else if (visible.length === 0) {
    body = (
      <EmptyState
        title="Никого не нашлось"
        text={query ? `По запросу «${query}» в этом фильтре никого нет.` : 'В этом фильтре никого нет.'}
        action={
          <button type="button" className="pc-btn" onClick={() => set({ q: null, f: null, g: null })}>
            Сбросить поиск
          </button>
        }
      />
    )
  } else {
    body = (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0 4px' }}>
          <p className="pc-muted" style={{ margin: 0, fontSize: 14, flex: 1 }} aria-live="polite">
            {selecting
              ? `Выбрано: ${selected.size}${hiddenChosen > 0 ? `, из них не в этом фильтре: ${hiddenChosen}` : ''}`
              : `Показано: ${visible.length}`}
          </p>
          {selecting ? (
            <>
              <button
                type="button"
                className="pc-btn"
                onClick={() =>
                  setSelected((prev) => {
                    const next = new Set(prev)
                    for (const a of visible) {
                      if (allVisibleChosen) next.delete(a.id)
                      else next.add(a.id)
                    }
                    return next
                  })
                }
              >
                {allVisibleChosen ? 'Снять показанных' : 'Все показанные'}
              </button>
              <button type="button" className="pc-btn" onClick={stopSelecting}>
                Готово
              </button>
            </>
          ) : (
            <button type="button" className="pc-btn" onClick={() => startSelecting()}>
              Выбрать
            </button>
          )}
        </div>
        <ul className="pc-list pc-card" style={{ padding: '0 12px' }}>
          {visible.map((a) => (
            <li key={a.id}>
              {selecting ? (
                <label className="pc-check-row" data-paused={isPaused(a) || undefined}>
                  <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggle(a.id)} />
                  <span className="pc-list-row__main">
                    <span className="pc-list-row__title" style={{ display: 'block' }}>
                      {a.name}
                    </span>
                    <span className="pc-list-row__meta">
                      {isPaused(a) ? 'На паузе / ' : ''}
                      {athleteMeta(a, groupName.get(groupOf(a) ?? ''), today)}
                    </span>
                  </span>
                </label>
              ) : (
                <button
                  type="button"
                  className="pc-list-row"
                  data-paused={isPaused(a) || undefined}
                  onPointerDown={() => pressStart(a.id)}
                  onPointerUp={pressEnd}
                  onPointerLeave={pressEnd}
                  onPointerCancel={pressEnd}
                  onContextMenu={(e) => e.preventDefault()}
                  onClick={() => {
                    // Долгое нажатие уже включило выбор - карточку не открываем
                    if (press.current.fired) {
                      press.current.fired = false
                      return
                    }
                    navigate(`/athletes/${a.id}`)
                  }}
                >
                  <span className="pc-list-row__main">
                    <span className="pc-list-row__title" style={{ display: 'block' }}>
                      {a.name}
                    </span>
                    <span className="pc-list-row__meta">
                      {isPaused(a) ? 'На паузе / ' : ''}
                      {athleteMeta(a, groupName.get(groupOf(a) ?? ''), today)}
                    </span>
                  </span>
                  <ChevronRight size={18} aria-hidden="true" className="pc-muted" />
                </button>
              )}
            </li>
          ))}
        </ul>
      </>
    )
  }

  return (
    <Screen
      title="Спортсмены"
      actionBar={
        selecting ? (
          <>
            <button type="button" className="pc-btn" style={{ flex: 1 }} disabled={chosen.length === 0} onClick={() => setBulk({ kind: 'move', athletes: chosen })}>
              В группу
            </button>
            <button type="button" className="pc-btn" style={{ flex: 1 }} disabled={chosen.length === 0} onClick={() => setBulk({ kind: 'pause', athletes: chosen })}>
              На паузу
            </button>
            <button
              type="button"
              className="pc-btn pc-btn--danger"
              style={{ flex: 1 }}
              disabled={chosen.length === 0}
              onClick={() => setBulk({ kind: 'delete', athletes: chosen })}
            >
              Удалить
            </button>
          </>
        ) : (
          <button
            type="button"
            className="pc-btn pc-btn--primary pc-btn--block"
            onClick={() => navigate(filter === 'group' && groupId ? `/athletes/new?g=${groupId}` : '/athletes/new')}
          >
            Добавить спортсмена
          </button>
        )
      }
    >
      <div className="pc-search">
        <label className="pc-visually-hidden" htmlFor="pc-athlete-search">
          Поиск по имени
        </label>
        <input
          id="pc-athlete-search"
          className="pc-input"
          type="search"
          placeholder="Поиск по имени"
          value={query}
          onChange={(e) => set({ q: e.target.value || null })}
          autoComplete="off"
        />
      </div>

      <div className="pc-segments" role="group" aria-label="Фильтр" style={{ marginTop: 12 }}>
        <button type="button" aria-pressed={filter === 'all'} onClick={() => set({ f: null, g: null })}>
          Все {all.length || ''}
        </button>
        <button type="button" aria-pressed={filter === 'group'} onClick={() => set({ f: 'group' })}>
          По группе
        </button>
        <button type="button" aria-pressed={filter === 'none'} onClick={() => set({ f: 'none', g: null })}>
          Без группы {withoutGroup || ''}
        </button>
      </div>

      {hubFilter && (
        <div className="pc-note" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ flex: 1 }}>
            Отбор Штаба{hubFilter.label ? `: ${hubFilter.label}` : ''}. В отборе {hubFilter.ids.length}.
          </span>
          <button
            type="button"
            className="pc-btn"
            onClick={() => navigate({ pathname: '/athletes', search: location.search }, { replace: true })}
          >
            Показать всех
          </button>
        </div>
      )}

      {filter === 'group' && (
        <div className="pc-chips" role="group" aria-label="Группа">
          {(groups.data ?? []).map((g) => (
            <button key={g.id} type="button" aria-pressed={groupId === g.id} onClick={() => set({ g: g.id })}>
              {g.name}
            </button>
          ))}
        </div>
      )}

      {body}

      {bulk && (
        <BulkSheet
          kind={bulk.kind}
          athletes={bulk.athletes}
          groups={groups.data ?? []}
          groupOf={groupOf}
          onClose={() => setBulk(null)}
          onFinished={stopSelecting}
        />
      )}
    </Screen>
  )
}
